(function () {
  if (customElements.get('hero-carousel-fs')) return;

  const SUPPORTS_INERT = 'inert' in HTMLElement.prototype;
  const FOCUSABLE = 'a[href], button, input, select, textarea, iframe, [tabindex]:not([tabindex="-1"])';

  class HeroCarouselFS extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.hcfs-slide'));
      this.dots = Array.from(this.querySelectorAll('.hcfs-dot'));
      this.prevBtn = this.querySelector('.hcfs-arrow--prev');
      this.nextBtn = this.querySelector('.hcfs-arrow--next');

      if (!this.slides.length) return;

      this.current = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.timer = null;
      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = parseInt(this.dataset.speed, 10) || 6000;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.touchStartX = null;

      this._onKeydown = this.onKeydown.bind(this);
      this._onVisibility = this.onVisibility.bind(this);

      this.dots.forEach((dot, i) => dot.addEventListener('click', () => { this.show(i); this.restart(); }));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => { this.next(); this.restart(); });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => { this.prev(); this.restart(); });

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.restart());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.restart(); });
      this.addEventListener('keydown', this._onKeydown);

      this.addEventListener('touchstart', (e) => { this.touchStartX = e.changedTouches[0].clientX; }, { passive: true });
      this.addEventListener('touchend', (e) => {
        if (this.touchStartX === null) return;
        const dx = e.changedTouches[0].clientX - this.touchStartX;
        if (Math.abs(dx) > 40) { dx < 0 ? this.next() : this.prev(); this.restart(); }
        this.touchStartX = null;
      }, { passive: true });

      document.addEventListener('visibilitychange', this._onVisibility);

      this.addEventListener('shopify:block:select', (e) => {
        const idx = this.slides.indexOf(e.target.closest('.hcfs-slide'));
        if (idx > -1) this.show(idx);
        this.stop();
      });
      this.addEventListener('shopify:block:deselect', () => this.restart());
      this.addEventListener('shopify:section:select', () => this.stop());
      this.addEventListener('shopify:section:deselect', () => this.restart());

      this.show(this.current);
      if (this.autoplay && this.slides.length > 1 && !this.reduceMotion.matches) this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    show(index) {
      // The slide leaving the stage may be holding focus (arrow keys are handled
      // on the carousel itself, so a keyboard user can trigger this from its CTA).
      // Note it before anything is made inert, and hand focus to the incoming
      // slide afterwards instead of dropping it on the body.
      const hadFocus = this.slides.some(
        (slide) => slide.contains(document.activeElement) && slide.classList.contains('is-active')
      );

      this.current = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, i) => {
        const active = i === this.current;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        // opacity:0 and pointer-events:none hide a slide from the eye and the
        // mouse but leave its CTA in the tab order, inside a container already
        // marked aria-hidden. Keyboard focus would land on a link nobody can see.
        this.setInert(slide, !active);
      });

      if (hadFocus) {
        const target = this.slides[this.current].querySelector(FOCUSABLE);
        if (target) target.focus();
      }
      this.dots.forEach((dot, i) => {
        const active = i === this.current;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    // `inert` is the correct tool and does the whole job in one attribute. Where
    // it is missing (Safari < 15.5, Firefox < 112) the fallback parks each
    // focusable descendant at tabindex="-1" and restores whatever it had before.
    setInert(slide, on) {
      if (SUPPORTS_INERT) {
        slide.inert = on;
        return;
      }
      slide.querySelectorAll(FOCUSABLE).forEach((el) => {
        if (on) {
          if (!el.hasAttribute('data-hcfs-tabindex')) {
            el.setAttribute('data-hcfs-tabindex', el.getAttribute('tabindex') || '');
          }
          el.setAttribute('tabindex', '-1');
        } else if (el.hasAttribute('data-hcfs-tabindex')) {
          const prev = el.getAttribute('data-hcfs-tabindex');
          if (prev === '') el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', prev);
          el.removeAttribute('data-hcfs-tabindex');
        }
      });
    }

    next() { this.show(this.current + 1); }
    prev() { this.show(this.current - 1); }

    start() {
      this.stop();
      if (this.slides.length < 2) return;
      this.timer = setInterval(() => this.next(), this.speed);
    }
    stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }
    restart() {
      if (this.autoplay && !this.reduceMotion.matches) this.start();
    }

    onKeydown(e) {
      if (e.key === 'ArrowRight') { this.next(); this.restart(); }
      else if (e.key === 'ArrowLeft') { this.prev(); this.restart(); }
    }

    onVisibility() {
      if (document.hidden) this.stop();
      else this.restart();
    }
  }

  customElements.define('hero-carousel-fs', HeroCarouselFS);
})();
